import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Paper,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { FileCopy, CheckCircle } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 400,
    padding: theme.spacing(2),
  },
  content: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  phoneInput: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  codeContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    textAlign: "center",
    width: "100%",
  },
  codeText: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    letterSpacing: "0.2em",
    color: theme.palette.primary.main,
    fontFamily: "monospace",
  },
  instructions: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  copyButton: {
    marginLeft: theme.spacing(1),
  },
}));

const PairPhoneModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [phone, setPhone] = useState("");
  const [linkingCode, setLinkingCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // ✅ Escutar eventos do socket para fechar o modal quando a conexão for estabelecida
  useEffect(() => {
    if (!whatsAppId || !socket || !user?.companyId || !open) return;

    const companyId = user.companyId;

    const onWhatsappData = (data) => {
      // Se receber atualização para este WhatsApp
      if (data.action === "update" && data.session.id === whatsAppId) {
        // Se o status mudou para CONNECTED ou o qrcode ficou vazio, fechar o modal
        if (data.session.status === "CONNECTED" || data.session.qrcode === "") {
          // Limpar estados e fechar
          setPhone("");
          setLinkingCode("");
          setCopied(false);
          onClose();
        }
      }
    };

    socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
    };
  }, [whatsAppId, socket, user?.companyId, open, onClose]);

  const handleRequestCode = async () => {
    if (!phone || phone.trim() === "") {
      toast.error("Por favor, insira um número de telefone");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post(`/whatsappsession/${whatsAppId}/pairphone`, {
        phone: phone.trim(),
      });

      setLinkingCode(data.linkingCode);
      toast.success("Código gerado com sucesso! Insira no WhatsApp para conectar.");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (linkingCode) {
      navigator.clipboard.writeText(linkingCode);
      setCopied(true);
      toast.success("Código copiado!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setPhone("");
    setLinkingCode("");
    setCopied(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="pair-phone-dialog"
    >
      <DialogTitle id="pair-phone-dialog">
        Conectar via Telefone
      </DialogTitle>
      <DialogContent className={classes.content}>
        {!linkingCode ? (
          <>
            <Typography variant="body1" gutterBottom>
              Digite o número de telefone do WhatsApp (com código do país):
            </Typography>
            <TextField
              className={classes.phoneInput}
              label="Número de Telefone"
              placeholder="Ex: 5511999999999"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              variant="outlined"
              fullWidth
              disabled={loading}
            />
            <Typography variant="body2" color="textSecondary">
              Exemplo: 5511999999999 (55 = Brasil, 11 = DDD, 999999999 = número)
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h6" color="primary" gutterBottom>
              Código de Pareamento
            </Typography>
            <Paper className={classes.codeContainer}>
              <Box display="flex" alignItems="center" justifyContent="center">
                <Typography className={classes.codeText}>
                  {linkingCode}
                </Typography>
                <IconButton
                  className={classes.copyButton}
                  onClick={handleCopyCode}
                  size="small"
                >
                  {copied ? (
                    <CheckCircle color="primary" />
                  ) : (
                    <FileCopy />
                  )}
                </IconButton>
              </Box>
            </Paper>
            <Typography className={classes.instructions}>
              <strong>Instruções:</strong>
              <br />
              1. Abra o WhatsApp no seu telefone
              <br />
              2. Vá em Configurações → Aparelhos conectados → Conectar um aparelho
              <br />
              3. Toque em "Conectar com número de telefone"
              <br />
              4. Digite o código acima quando solicitado
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          {linkingCode ? "Fechar" : "Cancelar"}
        </Button>
        {!linkingCode && (
          <Button
            onClick={handleRequestCode}
            color="primary"
            variant="contained"
            disabled={loading || !phone.trim()}
          >
            {loading ? "Gerando..." : "Gerar Código"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PairPhoneModal;

